// Issue announcements: the two draft emails (active members / recently
// expired) staff review before an issue goes out. Limits here mirror
// aamcirc/announcements/copy.py validate_copy so the Save button can explain
// a refusal before the server makes it.

export type Highlight = { title: string; hook: string; byline: string }
export type Copy = { subject: string; preheader: string; intro: string; cta_label: string; highlights: Highlight[] }
export type AudienceDetail = {
  id: number; audience: 'active' | 'expired'; copy: Copy; copy_source: 'ai' | 'fallback'
  campaign_id: number | null; campaign_status: string | null; scheduled_for: string | null
  rate_per_s: number | null; approved_by: string | null; approved_at: string | null
}
export type AnnouncementDetail = {
  edition: { id: number; publication_slug: string; title: string; issue_label: string
             cover_image_url: string | null; status: string }
  audiences: AudienceDetail[]
}

export const AUDIENCE_LABEL = { active: 'Active members', expired: 'Expired in the last six months' } as const

export const LIMITS = { subject: 70, preheader: 110, intro: 600, cta_label: 40, title: 120, hook: 200, byline: 120 } as const
export const MAX_HIGHLIGHTS = 4

/** Length as the server measures it: trimmed, in code points (Python len), not UTF-16 units. */
export const charCount = (s: string): number => [...s.trim()].length

export const canEdit = (status: string | null): boolean => status === 'draft'

export function copyProblems(c: Copy): string[] {
  const p: string[] = []
  if (!c.subject.trim()) p.push('Subject is empty')
  if (charCount(c.subject) > LIMITS.subject) p.push(`Subject is over ${LIMITS.subject} characters`)
  if (charCount(c.preheader) > LIMITS.preheader) p.push(`Preview text is over ${LIMITS.preheader} characters`)
  if (!c.intro.trim()) p.push('Intro is empty')
  if (charCount(c.intro) > LIMITS.intro) p.push(`Intro is over ${LIMITS.intro} characters`)
  if (!c.cta_label.trim()) p.push('Button label is empty')
  if (charCount(c.cta_label) > LIMITS.cta_label) p.push(`Button label is over ${LIMITS.cta_label} characters`)
  if (c.highlights.length && (c.highlights.length < 3 || c.highlights.length > MAX_HIGHLIGHTS))
    p.push('Use 3–4 highlights, or none')
  c.highlights.forEach((h, i) => {
    const n = i + 1
    if (!h.title.trim() || !h.hook.trim()) p.push(`Highlight ${n} needs a title and a hook`)
    if (charCount(h.title) > LIMITS.title) p.push(`Highlight ${n} title is over ${LIMITS.title} characters`)
    if (charCount(h.hook) > LIMITS.hook) p.push(`Highlight ${n} hook is over ${LIMITS.hook} characters`)
    if (charCount(h.byline) > LIMITS.byline) p.push(`Highlight ${n} byline is over ${LIMITS.byline} characters`)
  })
  return p
}

export const confirmPhrase = (sendable: number): string => `SEND ${sendable}`

export function moveHighlight(list: Highlight[], from: number, to: number): Highlight[] {
  const next = list.slice()
  const [h] = next.splice(from, 1)
  next.splice(to, 0, h)
  return next
}

/** A datetime-local value ("2026-10-06T09:30") as a full ISO string with an offset; null for "now"/blank. */
export function localInputToIso(local: string): string | null {
  if (!local) return null
  const d = new Date(local)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}
